/**
 * Task-related strings.
 */
import React from 'react';
import IconButton from '../ui/IconButton/IconButton';
import Icons from '../ui/IconButton/Icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {isMobileView} from "../lib/picos/browserFns";
import RoutedIconButton from "../ui/Buttons/RoutedIconButton";

const thumbsUp = <FontAwesomeIcon icon={Icons.THUMBS_UP.reactIcon} color='green'/>;
const thumbsDown = <FontAwesomeIcon icon={Icons.THUMBS_DOWN.reactIcon} color='maroon'/>;

export default {
	// Title
	d_title: 'Title',
	h_title: '<div><p>The title is a short description for the task, used when displaying the task in the Tasks page.</p><p>Use the Description field to enter a more thorough description to be used on invoices.</p><p>Note that the Description field will be the second field for non-billable tasks, and will be the first field in the Billable section for billable tasks.</p></div>',

	// Description
	d_description: 'Description',
	h_description: <div>
		<p>The description field is used to specify a more detailed description of the task.</p>
		<p>If this is a billable task, this will be the text included for the task in the invoice.</p>
		<p>You can configure this field to automatically populate with template text by configuring the Template
			Text field in the Activities tab (in the Configuration menu).</p>
		<p>If this is a billable entry, then there will be a paste <FontAwesomeIcon
			icon={Icons.PASTE.reactIcon}/> icon that will copy the text from the Title and paste it into the
			Description.</p>
		<p>
			<RoutedIconButton id='link-to-config'
						type={Icons.ACTIVITY}
						title='Configure Options'
						label='Configure Activities'
						route='/config/tasks/activities'
			/>
		</p>
	</div>,

	// Due Date
	d_dueDate: 'Due Date',
	h_dueDate: '<div><p>The due date specifies the time by which this task must be completed.</p><p>By default, the due date is mandatory, but the start date is not. If you want this task to show up at a specific time interval on the calendar, specify the start date in addition to the end date.</p></div>',

	// Estimated Time (duration)
	d_duration: 'Estimated Time',
	h_duration: <div>
		<p>This is the amount of time, in hours, that you estimate the task will require to complete. It is okay if
			the exact amount of time is not known, but it should be as accurate as possible.</p>
		<p>This number is used in the Task view to estimate your total work for the day. Looking above the task
			grid you will see two boxes, one with the total number of tasks for the day, and one for the total amount
			of time necessary to complete all of the tasks. The boxes will be color-coded to indicate the relative
			difficulty of completing all of the tasks on time.</p>
		<div style={{display: 'flex', flexDirection: 'row', gap: '2px', marginLeft: '5px', marginBottom: '10px'}}>
                <span key="numtasks" className='u-noWrap u-verticalCenter'>
                    <span className='badge badge-warning'>10 tasks</span>
                </span>
			<span key="duration" className='u-noWrap u-verticalCenter'>
                    <span className='badge badge-warning'>8+ hrs</span>
                </span>
		</div>
		<p>Because this field is not required, the estimated time to complete all tasks will show a "+" at the end
			of the time to indicate that they are tasks with no estimated time (effectively zero).</p>
	</div>,

	d_categories: 'Categories',
	h_categories: <div>
		<p>Categories </p>
		<p>The categories listed here relate to the categories in the Tasks filters, and tie deeply into other
			functionality in Outlaw Practice.</p>
		<p>Here are the major categories, and how they relate to the rest of the system:</p>
		<ul>
			<li><b>Client</b> - these are tasks related to a Case. If this category is chosen, then a Cases field
				will also be shown with a drop-down list of cases. By selecting a case, you have now tied the time
				for this task to the associated Case. If you switch to this case in the Cases page, you will see this
				time listed in the Time Entries section.
			</li>
			<li><b>Sales</b> - these are tasks related to a Lead. If this category is chosen, then a Leads field
				will also be shown with a drop-down list of leads. By selecting a lead, you have now tied the time for
				this task to the associated Lead. If you switch to the lead in the Leads page, you will see this time
				listed in the Time Entries section.
			</li>
			<li><b>Marketing</b> - these are tasks related to a Campaign. if this category is chosen, then a
				Campaigns field will also be shown with a drop-down list of Campaigns. By selecting a campaign, you have
				now tied the time for this task to the associated Campaign. If you switch to the campaign in the
				Marketing page, you will see this time listed in the Time Entries section.
			</li>
			<li><b>Human Resources</b> - these are tasks related to the hiring, firing, or maintenance of employees.
				For example, if you spend an hour teaching your paralegal, that time would go here. Or if you are
				creating a job description for a new secretary, that time would also go here. Any time you spend on your
				employees will generally end up in Human Resources.
			</li>
			<li><b>Administrative</b> - These are tasks that are not related to any other major area, and the majority
				of tasks for a secretary or receptionist. Examples would be ordering and restocking supplies,
				organizing your file cabinet or dealing with vendors. This also includes time organizing your tasks
				for the day!
			</li>
		</ul>
		<p>When you create a task from withing a campaign, lead or case, the associated category and it's related
			campaign, lead or case will automatically be selected. If you create a task from the main Tasks page, you
			can either create a task from scratch by clicking on the Add <FontAwesomeIcon
				icon={Icons.ADD.reactIcon}/> buttons in each grid section, or by selecting one of the <b>Task
				Templates</b>, which can be set to automatically select a category while leaving the
			associated campaign, lead or case empty.</p>
		<p>By default, a category is required for a task. If this is not desired, an
			administrator may turn off this requirement in Options.
		</p>
		<p>
			<RoutedIconButton id='link-to-config'
						type={Icons.CONFIG}
						title='Configure Categories'
						route='/config/tasks/categories'
						label='Configure Categories'
			/>
		</p>
	</div>,

	d_campaigns: 'Campaigns',
	h_campaigns: <>
		<div>Multiple campaign selections will result in time being split between
			the
			campaigns.
		</div>
		<div>You can create new campaigns on the Campaigns page.</div>
		<RoutedIconButton id='link-to-campaigns'
					type={Icons.HANDSHAKE}
					title='Go to Marketing'
					color='navy'
					label='Campaign Management'
					route='/marketing/campaigns'
		/>
		<div>By default, if a marketing-related category is selected, a campaign is
			required. If this is not desired, an administrator may turn off this
			requirement in Options.
		</div>
		<RoutedIconButton id='link-to-config'
					type={Icons.CONFIG}
					title='Configure Options'
					label='Options'
					route='/config/tasks/settings'
		/>
	</>,

	d_leads: 'Leads',
	h_leads: <>
		<div>Multiple lead selections will result in time being split between the
			leads.
		</div>
		<div>You can create new leads on the Leads page.</div>
		<RoutedIconButton id='link-to-leads'
					type={Icons.LEAD}
					title='Go to Sales'
					color='navy'
					label='Leads'
					route='/leads'
		/>
		<div>By default, if a sales-related category is selected, a lead is required. If this is not desired, an
			administrator may turn off this requirement in Options.
		</div>
		<RoutedIconButton id='link-to-config'
					type={Icons.CONFIG}
					title='Configure Options'
					route='/config/tasks/settings'
					label='Options'
		/>
	</>,

	d_cases: 'Cases',
	h_cases: <>
		<div>What case is this time for?</div>
		<div>You can create new cases on the Cases page.</div>
		<RoutedIconButton id='link-to-cases'
					type={Icons.CASE}
					title='Go to Cases'
					color='navy'
					label='Cases'
					route='/cases'
		/>
		<div>By default, if a client-related category is selected, a client is
			required. If this is not desired, an administrator may turn off this
			requirement in Options.
		</div>
		<RoutedIconButton id='link-to-config'
					title='Configure Options'
					type={Icons.CONFIG}
					label='Options'
					route='/config/tasks/settings'
		/>
	</>,

	d_billable: 'Billable',
	h_billable: '<div><p>If this task is related to a case (or an initial consult with a lead), this area shows the detailed billable parameters.</p><p>The <b>Description</b> field is the description that will be visible on the invoice.</p><p><b>No Charge</b> means that the cost is set to $0, but is left on the invoice.</p><p><b>Exclude From Invoice</b> means that the cost is set to $0, and is removed from the invoice. Never delete a task because it is zeroed-out, as we use these tasks to determine the total billed and paid for the case. Discounting your time to zero does not change the fact that you spent your valuable time on this task!</p><p>The remaining items show the labor rate and time, travel rate and time, and the total cost. Any and all of these can be overridden. For example, you may cap your travel time for a court appearance to 1 hour if you have a particularly long drive to the courthouse. Or you may decide that you can not reasonably charge the full time that you spent, so you lower the time. This means that you show the time on the invoice as the overridden amount, but for billed and paid the total time is considered billed. This is to help you understand the cost of writing down your hours or pay!</p></div>',

	d_activityType: 'Activities',
	h_activityType: <>
		<div>What type of activity is this?</div>
		<div>You can create new activity types in the Administration page.</div>
		<RoutedIconButton id='link-to-case-types'
					type={Icons.ACTIVITY}
					title='Configure Activity Types'
					color='navy'
					label='Activities'
					route='/config/tasks/activities'
		/>
		<div>By default, an activity is required for a task. If this is not desired, an administrator may turn off this
			requirement in Options.
		</div>
		<RoutedIconButton id='link-to-config'
					type={Icons.CONFIG}
					title='Configure Options'
					label='Options'
					route='/config/tasks/settings'
		/>
	</>,

	d_assignedTo: 'Assigned To',
	h_assignedTo: <div>
		<p>Which user <FontAwesomeIcon icon={Icons.USER.reactIcon}/> or group <FontAwesomeIcon
			icon={Icons.GROUP.reactIcon} color='blue'/> is this task assigned to?</p>
		<p>If a group is assigned, any member of that group may start the task, at which point it will be assigned
			to the user that started the timer.</p>
	</div>,

	d_participants: 'Participants',
	h_participants: '<div><p>Use this field to include people directly involved in the task.</p><p>For example, if you had a call with an opposing counsel or prosecutor, you can add their name here.</p><p>Note that you do not need to include the client or lead if this task is already associated with that case or lead, as it is redundant.</p><p>This field is included when searching using the search bar, making it easier to find all tasks related to the participant(s) entered here.</p></div>',

	d_startTime: 'Start Time',
	h_startTime: '<div><p>By specifying a start time, the task is changed from an all-day task on the calendar to a specific time block, determined by the start time and the estimated time specified above. </p></div>',

	d_impact: 'Impact',
	h_impact: `<div><p>Impact is the <b>relative impact on your business</b>, based on factors like <u>money, reputation or business risk</u>. </p><ul><li>High impact tasks have a significant positive impact on the business, such as a big payday if completed on time, or a significant negative impact, like a grievance or sanctions if a deadline has passed. </li><li>Medium impact tasks have a smaller impact on the business. For example, launching a new marketing campaign that you think will get you 10% more business over the next 6 months. </li><li>Low impact tasks do not effect the business much. For example, you might have a task to come up with a better list of drinks you want in the fridge. It's something you might need, but it isn't going to move the business one way or another. </li></ul><p>For example, if completing this task will result in a lot of money coming to your firm, that would be high impact. If failure to complete this task on time would result in a grievance, then this would also be high impact. </p><p>You can choose low, medium or high impact.</p></div>`,

	d_highImpact: 'Significant',
	h_highImpact: <span>High impact tasks have a significant positive impact on the business, such as a big payday if
		completed on time, or a significant negative impact, like a grievance or sanctions if a deadline has passed.
	</span>,

	d_mediumImpact: 'Moderate',
	h_mediumImpact: <span>Medium impact tasks have a smaller impact on the business. For example, launching a new
		marketing campaign that you think will get you 10% more business over the next 6 months.
	</span>,

	d_lowImpact: 'Not Much',
	h_lowImpact: <span>Low impact tasks do not effect the business much. For example, you might have a task to come up with
				a better list of drinks you want in the fridge. It's something you might need, but it isn't going to
				move the business one way or another.</span>,

	d_urgency: 'Urgency',
	h_urgency: '<div><p>Urgency is the timeframe in which a task must be completed.</p><ul><li>High urgency tasks need to be done very soon, today or tomorrow.</li><li>Medium urgency tasks probably need to be completed in a week or two.</li><li>Low urgency tasks either have a due date pretty far out, or no due date at all (get to it when you have time kind of tasks). </li></ul></div>',

	d_highUrgency: 'Right Away',
	h_highUrgency: <span>High urgency tasks need to be done very soon, today or tomorrow.</span>,

	d_mediumUrgency: 'Soon',
	h_mediumUrgency: <span>Medium urgency tasks probably need to be completed in a week or two.</span>,

	d_lowUrgency: 'Whenever',
	h_lowUrgency: <span>Low urgency tasks either have a due date pretty far out, or no due date at all (get to it when
				you have time kind of tasks).</span>,


	d_impactUrgency: 'Combined Impact and Urgency',
	h_impactUrgency: 'A combination of impact and urgency',

	d_private: <IconButton type={Icons.PRIVATE} label='Private' labelOnLeft={true}/>,
	h_private: '<div><p>Private entries are only visible to the user that created them (unless they are assigned to another user). These are useful for sensitive information, or personal tasks do not need to be seen by others.</p></div>',

	d_docketId: 'Docket',
	h_docketId: <>
		<div>On which docket will this case be heard?</div>
		<RoutedIconButton id='link-to-dockets'
					type={Icons.GAVEL}
					label='Configure Dockets'
					route='/config/cases/dockets'
		/>
	</>,

	d_roomNumber: 'Room Number',
	h_roomNumber: '<div><p>If there is a room that this docket is assigned to, it will appear here.</p><p>This will default to the room specified in the Docket, but it can be overridden. </p></div>',

	d_invoiceOptions: 'Options',
	h_invoiceOptions: '<div><p><b>No Charge</b> will show the time entry on the invoice, but the amount will be set to $0.</p><p><b>Exclude From Invoice</b> will hide the time entry from the invoice entirely, and the amount will be set to $0.</p></div>',

	d_noCharge: 'No Charge',
	h_noCharge: <div>
		<b>No Charge</b> will show the time entry on the invoice, but the amount will be set to $0.
	</div>,

	d_excludeFromInvoice: 'Exclude From Invoice',
	h_excludeFromInvoice: <div>
		<b>Exclude From Invoice</b> will hide the time entry from the invoice entirely, and the amount will be set to
		$0.
	</div>,

	d_cost: 'Total Cost',
	h_cost: <div>
		<p>This is the total calculated cost of the time entry, based on the billing increments and rounding method
			set in Invoicing Options, as well as the No Charge, Exclude from Invoice and Overrides.</p>
		<p>Note that flat fee entries will always show $0.</p>
		<p>
			<RoutedIconButton id='link-to-invoicing'
						type={Icons.INVOICE}
						label='Configure Invoicing'
						route='/config/financial/invoicing?div=billing'
			/>
		</p>
		<p>The total cost can be overridden by checking the Override checkbox to the right of the amount.</p>
	</div>,

	d_flatFee: 'Flat Fee',
	h_flatFee: '<div><p>If this task is related to a flat fee (available by setting the Fee Type(s) in the Case to include Flat Fee, and creating flat fees in the Flat Fees section of the Case), then a list of available Flat Fees will be listed here.</p><p>When associated with a Flat Fee, the total for this entry will be $0.</p></div>',

	d_laborRate: 'Labor Rate',
	h_laborRate: '<div><p>Specifies the labor rate in effect, which defaults to the billable rate for the user that the task is assigned to. (This can be modified in the Case using the Hourly Rates section).</p><p>Using these fields you can separate the cost of labor vs. the cost of travel, if you separate the two.</p><p>You can override this rate by clicking the Override button to the right of the Labor Rate field.</p><p>This will use the override rate and the Labor Time when calculating the total cost.</p></div>',

	d_travelRate: 'Travel Rate',
	h_travelRate: '<div><p>Specifies the travel rate in effect, which defaults to the billable rate for the user that the task is assigned to.</p><p>Using these fields you can separate the cost of labor vs. the cost of travel, if you separate the two.</p><p>You can override this rate by clicking the Override button to the right of the Labor Rate field.</p><p>This will use the override rate and the Travel Time when calculating the total cost.</p></div>',

	d_laborTime: 'Labor Time',
	h_laborTime: '<div><p>This is the portion of the total time allocated to labor (as opposed to travel).</p></div>',

	d_travelTime: 'Travel Time',
	h_travelTime: '<div><p>This is the portion of the total time allocated for travel (as opposed to labor).</p></div>',

	d_recentlyModified: 'Recently Modified Tasks',
	h_recentlyModified: <div>
		<p>Shows the last 50 tasks that have been modified in any way.</p>
	</div>,

	d_timeEntries: 'Time Entries',
	h_timeEntries: (params: {entityName: string}) => `<div><p>Time entries (tasks) related to this ${params.entityName}.</p></div>`,

	d_labor: 'Labor',
	h_labor: 'Time entries (tasks) related to this Invoice, along with the associated costs of that labor.',

	d_laborCosts: 'Labor Costs',
	h_laborCosts: 'Time entries (tasks) related to this Campaign, along with the associated costs of that labor.',

	d_convertToTemplate: 'Create Template from this Task',
	h_convertToTemplate: '<div><p>Click here to turn this task into a task template. After conversion, you will see the new template in the Task Templates section (left pane of the Task view).</p><p>The name of the template will be the title of this task, and as much information as possible will be saved in the template.</p></div>',

	d_global: 'Global',
	h_global: 'Should this template be visible to everyone?',

	d_notesDisabled: 'Time entry is part of finalized invoice, read-only',

	/* Task Options */
	d_showFilters: isMobileView() ? 'Show Filters' : 'Show Filter Sidebar',
	h_showFilters: <div>
		<p>Show or hide filters for Task Categories, Users, and Task Templates.</p>
	</div>,

	d_showGridNumbers: 'Show Grid Numbers',
	h_showGridNumbers: 'Show the Grid Numbers (1-9) in the Task Grid. These numbers help you understand the order in whi you should address each task: Tasks in grid 1 (high priority, high urgency) should be finished before work on grid 2 (high urgency, medium impact).',

	d_ringBellOnCompletion: 'Ring Bell on Completion',
	h_ringBellOnCompletion: <div>
		<p>When a task is finished, a bell will ring to let you (and your team!) know that you are
			making progress.</p>
		<p>You can turn this off when you need quiet, or if you dislike the sound of success.</p>
	</div>,

	d_showIncompleteTasks: 'Show Incomplete Tasks',
	h_showIncompleteTasks: <div>
		<p>Selecting this toggle will ensure that tasks that have not been completed are visible.</p>
		<p>If you want to focus on completed tasks only, toggle this switch off.</p>
	</div>,

	d_showCompletedTasks: 'Show Completed Tasks',
	h_showCompletedTasks: <div>
		<p>Selecting this toggle will ensure that tasks that have been completed are visible.</p>
		<p>If you want to focus on incomplete tasks only, toggle this switch off.</p>
	</div>,

	d_lookback: 'Lookback',
	h_lookback: <div>
		<p>If you want to see completed tasks, you must toggle on the Show Completed Tasks toggle,
			as well as set a maximum lookback.</p>
		<p>The default is 1 Day, although you can specify 1 week, 1 month, 1 year, or All tasks.</p>
		<p>Larger intervals, like 1 year or All, require that you enter something in the Search bar to
			limit the number of tasks. Putting 40,000 tasks on the screen is a very bad idea, so we make sure
			you are focusing on a specific subset of tasks, like client name.</p>
	</div>,

	d_showDateLabels: 'Show Date Labels',
	h_showDateLabels: '<div><p>Toggle this switch to turn on/off date labels in the task description. The date will show up in white at the beginning of a task if enabled.</p></div>',

	d_showClientParticipantNames: 'Show Client/Participant Names',
	h_showClientParticipantNames: '<div><p>Toggle this switch to show Client and/or participant names in the task description.</p><p>Client names are added if this task is related to a Lead or a Case.</p><p>Participant names are added to a task in the Participant section. Participants are generally used to indicate individuals that were directly involved, such as people on a call, or in a meeting.</p></div>',

	d_caseFinancialSummary: 'Financials',
	h_caseFinancialSummary: 'Quickly view the financial summary for the related case.',

	/* Notification-related strings */
	d_notification: 'For Review',

	/* Color schemes:
	 * Status     From User             Target User             Meaning
	 * ------     -------------------   ----------------------  ---------------------------------------
	 * Review     Outline Red           Filled Red              Review requested by the firm's owner
	 * Waiting    Outline Orange        Filled Orange           Changes made by assignee, awaiting review by firm owner
	 * Complete   Thin Checkered Flag   DuoTone Checkered Flag  Firm's owner has approved changes
	 * null       Outline Black                                 No notification has been applied
	 */
	h_notification: <div>
		<p>This task has been marked for review.</p>
		<p>The status of a flag can be determined by its color and color fill (outline or solid). In general, if the
			flag is filled, you are the recipient of the notification. If the flag is outlined, you are the one that has
			triggered the flag.</p>
		<p>The color of the flag indicates the status of the notification.</p>

		<IconButton type={Icons.FLAG}
					color='black'
					label='Black means the task has no notifications, and can be clicked by the owner/attorney to start a notification.'/>
		<IconButton type={Icons.FLAG}
					color='maroon'
					label='Red outlined means the owner has marked the task for review, and the owner is viewing it.'/>
		<IconButton type={Icons.FLAG_FILLED}
					color='maroon'
					label='Red filled means the owner has marked the task for review, and the assignee is viewing it.'/>
		<IconButton type={Icons.FLAG}
					color='orange'
					label='Orange outlined means the task has been revised by you, and is waiting for the owner to approve it.'/>
		<IconButton type={Icons.FLAG_FILLED}
					color='orange'
					label='Orange filled means the task has been revised by the assignee, and you need to approve it.'/>
		<IconButton type={Icons.FLAG_CHECKERED}
					color='black'
					label='Checkered flag means that the revisions to the task have been approved. Both the owner and the assignee see the same flag.'/>

		<p>When you have finished editing the task, click the {thumbsUp} to continue.</p>
	</div>,

	// This is the default message for when a task is marked by an owner for review
	d_notificationPleaseReview: 'Please Review',

	d_notificationCancel: 'Remove this notification',
	h_notificationCancel: <div>
		<p>This will remove the notification</p>
	</div>,

	// These are shown to the target
	d_notificationStatusReview: 'This task has been marked for you to review. Make revisions, then click on {thumbsUp} to finish.',

	d_notificationStatusWaiting: "This task is waiting for your manager's approval.",

	// These are shown to the owner
	d_notificationStatusReviewOwner: 'You have marked this task for review.',

	d_notificationStatusWaitingOwner: <div>This task is waiting for your approval.
		Click on {thumbsUp} to approve, {thumbsDown} to send back for revision.</div>,

	// Shown to both
	d_notificationStatusComplete: 'This task has been reviewed, updated and approved',
};
